import {
  B2BIdentityProvider,
  useStytchMemberSession,
  useStytchOrganization,
} from '@stytch/react/b2b';
import { useEffect, useRef, useState } from 'react';
import { clearStytchCookies } from '../providers/clearStytchCookies';
import { useStytch } from '../providers/StytchContext';
import {
  assertSessionWithinPolicy,
  resolveSessionDurationMinutes,
  resolveSessionPolicyDeadline,
  SessionPolicyError,
} from '../sessionPolicy';
import { selectOrganization } from '../utils/selectOrganization';

const OAUTH_PARAMS_KEY = 'connected_app_oauth_params';
const MAX_TIMEOUT_MILLISECONDS = 2_147_483_647;

/**
 * Self-contained OAuth authorization page for Stytch Connected Apps.
 *
 * This component handles its own Stytch login inline — it does NOT redirect
 * to /login or depend on AuthProvider, DiscoveryCallback, or LoginPage.
 *
 * When no Stytch session exists:
 * 1. Saves the current OAuth query params to sessionStorage.
 * 2. Renders a Google login button.
 * 3. Starts Stytch discovery OAuth with redirect back to /oauth/authorize.
 * 4. On return, detects the discovery token, authenticates, exchanges for
 *    a Stytch B2B member session, restores OAuth params, and renders
 *    B2BIdentityProvider.
 *
 * When a Stytch session already exists, renders B2BIdentityProvider immediately.
 *
 * The Moments backend is never called (no tokenService.exchangeStytchSession),
 * so the Stytch session token is never rotated by Sessions.Authenticate.
 *
 * Mount at /oauth/authorize and register that URL in the Stytch Dashboard
 * under both Connected Apps > Authorization URL and Redirect URLs (Discovery).
 */
export default function ConnectedAppAuthorize() {
  const { ready } = useStytch();

  if (!ready) {
    return null;
  }

  return <ConnectedAppAuthorizeInner />;
}

function ConnectedAppAuthorizeInner() {
  const { session, isInitialized } = useStytchMemberSession();
  const { organization, isInitialized: isOrganizationInitialized } = useStytchOrganization();
  const { stytch } = useStytch();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasInitiated = useRef(false);
  const [validatedSessionPolicyKey, setValidatedSessionPolicyKey] = useState<string | null>(null);
  const sessionPolicyKey =
    session && organization
      ? [
          session.member_session_id,
          session.member_id,
          session.started_at,
          organization.organization_id,
          organization.trusted_metadata?.mento_session_duration_minutes,
        ].join('|')
      : null;

  useEffect(() => {
    if (!session || !organization || !isOrganizationInitialized || !stytch || !sessionPolicyKey) {
      return;
    }

    let timeoutId: number | undefined;
    let cancelled = false;

    const rejectSession = async (policyError: unknown) => {
      setValidatedSessionPolicyKey(null);
      await stytch.session.revoke().catch(() => undefined);
      await clearStytchCookies();
      if (!cancelled) {
        setError(
          policyError instanceof Error ? policyError.message : 'Invalid organization session policy'
        );
      }
    };

    const enforceActiveSessionPolicy = async () => {
      try {
        assertSessionWithinPolicy(organization, session.started_at);
        const deadline = resolveSessionPolicyDeadline(organization, session.started_at);
        setError(null);
        setValidatedSessionPolicyKey(sessionPolicyKey);

        const scheduleDeadlineCheck = () => {
          const remainingMilliseconds = deadline.getTime() - Date.now();
          if (remainingMilliseconds <= 0) {
            void rejectSession(
              new SessionPolicyError('Your session has expired. Please sign in again.')
            );
            return;
          }
          timeoutId = window.setTimeout(
            scheduleDeadlineCheck,
            Math.min(remainingMilliseconds, MAX_TIMEOUT_MILLISECONDS)
          );
        };
        scheduleDeadlineCheck();
      } catch (policyError) {
        await rejectSession(policyError);
      }
    };

    void enforceActiveSessionPolicy();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isOrganizationInitialized, organization, session, sessionPolicyKey, stytch]);

  // Process discovery callback token if present in URL params.
  useEffect(() => {
    if (!isInitialized || !stytch || hasInitiated.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const tokenType = params.get('stytch_token_type');

    if (!token || tokenType !== 'discovery_oauth') {
      return;
    }

    hasInitiated.current = true;
    setIsProcessing(true);

    const processDiscoveryToken = async () => {
      try {
        await clearStytchCookies();

        const authResp = await stytch.oauth.discovery.authenticate({
          discovery_oauth_token: token,
        });

        const { intermediate_session_token, discovered_organizations } = authResp;
        const discoveredEmail =
          typeof (authResp as { email_address?: unknown }).email_address === 'string'
            ? ((authResp as { email_address: string }).email_address || '').trim().toLowerCase()
            : '';

        if (!intermediate_session_token) {
          setError('No intermediate session token received');
          setIsProcessing(false);
          return;
        }

        if (!discovered_organizations || discovered_organizations.length === 0) {
          setError('Your account is not pre-approved. Contact your administrator.');
          setIsProcessing(false);
          return;
        }

        const selectedOrg = selectOrganization(discovered_organizations, discoveredEmail);
        if (!selectedOrg) {
          setError('Multiple organizations found. Contact support.');
          setIsProcessing(false);
          return;
        }

        await stytch.discovery.intermediateSessions.exchange({
          intermediate_session_token,
          organization_id: selectedOrg.organization.organization_id,
          session_duration_minutes: resolveSessionDurationMinutes(selectedOrg.organization),
        } as any);

        // Session is now active. Restore the original OAuth params so
        // B2BIdentityProvider can read client_id, redirect_uri, etc.
        // Use history.replaceState (no page reload) so the Stytch SDK
        // keeps the session in memory. A full page reload would require
        // re-hydration from cookies, which races with initialization.
        const savedParams = sessionStorage.getItem(OAUTH_PARAMS_KEY);
        if (savedParams) {
          sessionStorage.removeItem(OAUTH_PARAMS_KEY);
          window.history.replaceState({}, '', '/oauth/authorize' + savedParams);
        }

        // Let React re-render — session is in memory, B2BIdentityProvider
        // will read OAuth params from the updated URL.
        setIsProcessing(false);
      } catch (e: any) {
        await clearStytchCookies();
        console.error('Connected App discovery error:', e);
        setError(e?.message || 'Authentication failed');
        setIsProcessing(false);
      }
    };

    processDiscoveryToken();
  }, [isInitialized, stytch]);

  if (
    !isInitialized ||
    isProcessing ||
    (session &&
      (!isOrganizationInitialized || validatedSessionPolicyKey !== sessionPolicyKey) &&
      !error)
  ) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.text}>Signing in...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ ...styles.text, color: '#d32f2f' }}>{error}</p>
          <button
            type="button"
            onClick={() => {
              setError(null);
              hasInitiated.current = false;
              // Clear stale discovery token from URL so the effect doesn't
              // re-attempt authentication with an already-consumed token.
              const saved = sessionStorage.getItem(OAUTH_PARAMS_KEY);
              if (saved) {
                window.history.replaceState({}, '', '/oauth/authorize' + saved);
              } else {
                window.history.replaceState({}, '', '/oauth/authorize');
              }
            }}
            style={styles.retryButton}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Session exists — render Connected App consent.
  if (session) {
    return <B2BIdentityProvider />;
  }

  // No session, no discovery token in URL — show login button.
  return <ConnectedAppLogin />;
}

function ConnectedAppLogin() {
  const { stytch } = useStytch();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleLogin = (provider: 'google' | 'microsoft') => {
    if (!stytch) {
      setLoginError('Authentication not ready');
      return;
    }
    setIsAuthenticating(true);
    setLoginError(null);

    // Save the current OAuth query params before redirecting.
    if (window.location.search) {
      sessionStorage.setItem(OAUTH_PARAMS_KEY, window.location.search);
    }

    // Start discovery FIRST — this is synchronous and sets window.location.
    // Do NOT await async cleanup before this call, because clearStytchCookies
    // triggers SDK state changes that unmount this component mid-await.
    try {
      const startFn =
        provider === 'microsoft' ? stytch.oauth.microsoft.discovery : stytch.oauth.google.discovery;
      startFn.start({
        discovery_redirect_url: `${window.location.origin}/oauth/authorize`,
      });
    } catch (e: any) {
      setLoginError(e?.message || 'Failed to start login');
      setIsAuthenticating(false);
    }
  };

  const providers = resolveProviders();
  const hasMicrosoft = providers.includes('microsoft');

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Sign in to authorize</h2>
        <p style={styles.subtext}>Sign in with your company account to continue.</p>

        {loginError && <p style={{ ...styles.text, color: '#d32f2f' }}>{loginError}</p>}

        <button
          type="button"
          onClick={() => handleLogin('google')}
          disabled={isAuthenticating}
          style={styles.loginButton}
        >
          {isAuthenticating ? 'Signing in...' : 'Continue with Google'}
        </button>

        {hasMicrosoft && (
          <button
            type="button"
            onClick={() => handleLogin('microsoft')}
            disabled={isAuthenticating}
            style={{ ...styles.loginButton, marginTop: '8px' }}
          >
            {isAuthenticating ? 'Signing in...' : 'Continue with Microsoft'}
          </button>
        )}
      </div>
    </div>
  );
}

function resolveProviders(): string[] {
  if (typeof window === 'undefined') {
    return ['google'];
  }
  const raw = window.__CONFIG__?.STYTCH_DISCOVERY_PROVIDERS;
  if (!Array.isArray(raw) || raw.length === 0) {
    return ['google'];
  }
  return raw
    .map((p: string) => p.trim().toLowerCase())
    .filter((p: string) => p === 'google' || p === 'microsoft');
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    alignItems: 'center',
    background:
      'linear-gradient(-6deg, rgba(80, 90, 84, 0.8) 28%, rgba(189, 204, 190, 0.7) 100%), #000000',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '24px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    maxWidth: '400px',
    padding: '40px 32px',
    textAlign: 'center' as const,
    width: '100%',
  },
  heading: {
    color: '#1f1f1f',
    fontFamily: "'Basis Grotesque', system-ui, sans-serif",
    fontSize: '20px',
    fontWeight: 600,
    margin: '0 0 8px 0',
  },
  subtext: {
    color: '#666',
    fontFamily: "'Basis Grotesque', system-ui, sans-serif",
    fontSize: '14px',
    margin: '0 0 24px 0',
  },
  text: {
    color: '#1f1f1f',
    fontFamily: "'Basis Grotesque', system-ui, sans-serif",
    fontSize: '14px',
  },
  loginButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    color: '#1f1f1f',
    cursor: 'pointer',
    display: 'block',
    fontFamily: "'Basis Grotesque', system-ui, sans-serif",
    fontSize: '14px',
    fontWeight: 500,
    padding: '12px 24px',
    width: '100%',
  },
  retryButton: {
    backgroundColor: 'transparent',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    color: '#1f1f1f',
    cursor: 'pointer',
    fontFamily: "'Basis Grotesque', system-ui, sans-serif",
    fontSize: '14px',
    marginTop: '16px',
    padding: '8px 20px',
  },
};
